import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plane, Calendar, MapPin, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="my-bookings-title">My Bookings</h1>
            <p className="text-lg text-slate-600">View and manage your flight bookings</p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading bookings...</p>
            </div>
          ) : bookings.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="p-12 border-slate-200 text-center" data-testid="no-bookings-message">
                <Plane className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">No bookings yet</h2>
                <p className="text-slate-600 mb-6">Start your journey by booking your first flight</p>
                <a href="/book-flight" className="inline-block px-6 py-3 bg-amber-600 text-white rounded-full font-semibold hover:bg-amber-700 transition-colors" data-testid="book-first-flight-link">
                  Book Your First Flight
                </a>
              </Card>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {bookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="p-6 border-slate-200 hover-lift" data-testid={`booking-card-${index}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            {booking.status}
                          </div>
                          <span className="text-sm text-slate-600">PNR: {booking.pnr}</span>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <Plane className="w-5 h-5 text-slate-600" />
                            <span className="font-semibold text-slate-900">Flight Details</span>
                          </div>

                          <div className="flex items-center gap-3">
                            <MapPin className="w-5 h-5 text-slate-600" />
                            <span className="text-slate-700">Route info</span>
                          </div>

                          <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-slate-600" />
                            <span className="text-slate-700">
                              Booked on: {new Date(booking.booking_date).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm text-slate-600 mb-1">Passenger</p>
                          <p className="font-semibold text-slate-900">{booking.passenger_name}</p>
                          <p className="text-sm text-slate-600">{booking.passenger_email}</p>
                        </div>
                      </div>

                      <div className="text-center lg:text-right">
                        <div className="w-32 h-32 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-3">
                          <Plane className="w-12 h-12 text-slate-600" />
                        </div>
                        <p className="text-sm text-slate-600 mb-1">Booking ID</p>
                        <p className="text-xs font-mono text-slate-700">{booking.id.slice(0, 8)}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
